import React, { useState } from 'react';
import { PiEnvelopeSimple, PiX } from 'react-icons/pi';

// Grace-period escalation, second stage. The first few days after signup, an
// unverified account only shows a quiet dot on the avatar (App.jsx's
// Header) — nothing here. Once GRACE_PERIOD_DAYS has passed and it's still
// unverified, this bar takes over: still non-blocking (browsing/messaging
// stay open), but visible enough that the eventual hard gate on payouts/
// publishing/posting a job isn't a total surprise the first time someone
// hits it. Dismiss is per-session only — an unverified account past the
// grace period is a standing thing to resolve, not a one-time notice.
const GRACE_PERIOD_DAYS = 3;

export default function EmailVerifyReminder({ user }) {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('emailVerifyReminderDismissed') === '1');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user.emailVerified !== false || dismissed) return null;

  const daysSinceSignup = user.createdAt ? (Date.now() - new Date(user.createdAt).getTime()) / 86400000 : 0;
  if (daysSinceSignup < GRACE_PERIOD_DAYS) return null;

  const isEduAddress = /\.edu$/i.test(user.email || '');

  const dismiss = () => {
    sessionStorage.setItem('emailVerifyReminderDismissed', '1');
    setDismissed(true);
  };

  const resend = async () => {
    setSending(true);
    try {
      const res = await fetch('/api/users/resend-verification', { method: 'POST', credentials: 'include' });
      if (res.ok) setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-warning/30 bg-warning/10 px-4 py-2.5 text-sm text-dark">
      <PiEnvelopeSimple size={15} className="flex-none text-warning" />
      <span className="text-center">
        {sent
          ? 'Verification email sent. Check your inbox.'
          : 'Still haven’t verified your email? It unlocks payouts, publishing, and posting jobs.'}
        {!sent && isEduAddress && ' University inboxes often send it to Junk/Spam.'}
      </span>
      {!sent && (
        <button
          type="button"
          onClick={resend}
          disabled={sending}
          className="flex-none rounded-full border border-dark/15 bg-white px-3 py-1 text-xs font-semibold text-dark transition hover:bg-dark hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sending ? 'Sending…' : 'Resend email'}
        </button>
      )}
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="flex-none text-dark/50 transition hover:text-dark">
        <PiX size={15} />
      </button>
    </div>
  );
}
